import React, { useEffect, useState } from 'react'
import {
    Box,
    Link,
    Spacer,
    Text
} from '@chakra-ui/react'
import { useProducts } from 'src/services/api/queries/queries';
import ProductList from 'src/pages/ProductsPage/components/ProductList'
import { useParams } from 'react-router-dom';
import { WCRestAPI, WPRestAPI } from 'src/services/api/ApiService';

interface LabelProps {
    text: string;
}

const StorePropertyLabel: React.FC<LabelProps> = ({ text }) => {
    return (
        <Text fontSize="1.2rem" fontWeight="bold" color="#1d6973" >
            {text}
        </Text>
    );
};

const HeaderLabel: React.FC<LabelProps> = ({ text }) => {
    return (
        <Text fontSize="2.25rem" fontWeight="bold" marginTop="4rem" marginBottom="4rem" color="#1d6973">
            {text}
        </Text>
    );
};

const CategoryLabel: React.FC<LabelProps> = ({ text }) => {
    return (
        <Text fontSize="1.5rem" fontWeight="bold" color="gray" marginBottom="2rem">
            {text}
        </Text>
    );
};

const filterKeys = {
    'recser': '83', // Battery recycling
    'elker': '82', // SER recycling
    'elker-data-ser': '92', // SER recycling
    'recser-packaging': '79', // Packaging
    'elker-data-ser-packaging': '91', // Packaging
}

const batteryRecyclingFilters = {
    category: [filterKeys['recser']]
}

const serRecyclingFilters = {
    category: [filterKeys['elker'], filterKeys['elker-data-ser']]
}

const emptyEquipmentFilters = {
    category: [filterKeys['recser-packaging'], filterKeys['elker-data-ser-packaging']]
}

type CollectionPointInfoProps = {
    storeId: string;
}

const CollectionPointInfo = ({ storeId }: CollectionPointInfoProps) => {
    const [name, setName] = useState<string>("");
    const [address, setAddress] = useState<string>("");
    const [email, setEmail] = useState<string>("");

    const fetchUserInfo = () => {
        WCRestAPI.get(`/customers?search=${storeId}`)
            .then(response => {
                if (response.data && response.data.length > 0) {
                    setName(response.data[0]?.billing?.company);
                    setAddress(
                        response.data[0]?.billing?.address_1 + ', '
                        + response.data[0]?.billing?.postcode + ' '
                        + response.data[0]?.billing?.city
                    );
                    setEmail(response.data[0]?.billing?.email);
                }
            })
    };

    useEffect(() => {
        fetchUserInfo();
    }, []);

    return (
            <Box>
                <Text fontSize="2.25rem" fontWeight="bold" marginTop="2rem" marginBottom="1rem">
                    Collection point information
                </Text>
                <Box
                    display="flex"
                    flexDirection="column"
                    padding="3rem 4rem"
                    backgroundColor="white"
                    borderRadius="30px"
                >
                    <StorePropertyLabel text="Name:"/>
                    <Text marginBottom="1.5rem">{name}</Text>
                    
                    <StorePropertyLabel text="Address:"/>
                    <Text marginBottom="1.5rem">{address}</Text>

                    <StorePropertyLabel text="Email address:"/>
                    <Text>{email}</Text>
                </Box>
            </Box>
    );
};

type ProductsListProps = {
    isUnregistered?: boolean;
}

const ProductsList = ({ isUnregistered }: ProductsListProps) => {
    // TODO: use a single query and filter on frontend to reduce network load
    const { data: batteryRecyclingProducts } = useProducts({ filters: batteryRecyclingFilters })
    const { data: serRecyclingProducts } = useProducts({ filters: serRecyclingFilters })
    const { data: emptyEquipmentProducts } = useProducts({ filters: emptyEquipmentFilters })

    return (
        <Box>
            <HeaderLabel text="Order pickup for the products below" />
            <CategoryLabel text="Battery recycling" />
            {
                isUnregistered
                && (
                    <Box marginBottom="2rem">
                        <Box>
                            <Text>
                                In order to be able to order pickups of battery drums, 
                                empty battery drums or accessories at your collection point, 
                                you need your own store number for your collection point. 
                                If battery barrels are already in use at your collection point, 
                                contact the battery recycling customer service at info@recser.fi 
                                to get the store number. As a new barrel collection point, 
                                we ask you to register at:
                            </Text>
                        </Box>
                        <Box>
                            <Text>
                                <Link href="https://www.paristokierratys.fi/kerayspisteille/paristotynnyrin-tilaus">
                                    https://www.paristokierratys.fi/kerayspisteille/paristotynnyrin-tilaus
                                </Link>
                                {' '}(registration is free). You can place an order 
                                when you have received your location-specific store number.
                            </Text>
                        </Box>
                    </Box>
                )
            }
            <ProductList 
                products={batteryRecyclingProducts}
                loadMore={() => {}}
                hasNextPage={false}
                isFetchingNextPage={false}
                isWastePortal
                isUnregisteredUserOnWastePortal={isUnregistered}
            />
            <Spacer height="6rem" />
            <CategoryLabel text="SER recycling" />
            <ProductList 
                products={serRecyclingProducts}
                loadMore={() => {}}
                hasNextPage={false}
                isFetchingNextPage={false}
                isWastePortal
                isUnregisteredUserOnWastePortal={isUnregistered}
            />
    
            <HeaderLabel text="Order empty transport/collection equipment and accessories" />
            <ProductList 
                products={emptyEquipmentProducts}
                loadMore={() => {}}
                hasNextPage={false}
                isFetchingNextPage={false}
                isWastePortal
                isUnregisteredUserOnWastePortal={isUnregistered}
            />
        </Box>
    );
};

export const WasteCollectionPage = () => {
    const { storeId } = useParams();
    const isUnregistered = storeId === 'unregistered';
    return (
        <Box display="flex" justifyContent="center">
            <Box width="90%">
                {
                    !isUnregistered
                    && <CollectionPointInfo storeId={storeId} />
                }
                <ProductsList isUnregistered={isUnregistered} />
            </Box>
        </Box>
    );
};
